/**
 * Amplitude tracking provider
 */

import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser'
import type {
  TrackingProvider,
  UserProperties,
  GroupProperties,
} from '../types'

const AMPLITUDE_API_KEY = '8318f0cbdf30d4fad832a57603794494'

type AmplitudeInstance = typeof import('@amplitude/unified')

let amplitude: AmplitudeInstance | null = null
let initPromise: Promise<void> | null = null

export function createAmplitudeProvider(): TrackingProvider {
  async function ensureInit(): Promise<AmplitudeInstance> {
    if (amplitude) return amplitude

    if (!initPromise) {
      initPromise = (async () => {
        const amp = await import('@amplitude/unified')
        await amp.initAll(AMPLITUDE_API_KEY, {
          analytics: {
            autocapture: {
              attribution: true,
              fileDownloads: true,
              formInteractions: true,
              pageViews: false,
              sessions: true,
              elementInteractions: true,
              networkTracking: true,
              webVitals: true,
              frustrationInteractions: true,
            },
          },
          sessionReplay: { sampleRate: 1 },
        })
        amplitude = amp
      })()
    }

    await initPromise
    return amplitude!
  }

  return {
    name: 'amplitude',

    async init(): Promise<void> {
      await ensureInit()
    },

    async reset(): Promise<void> {
      const amp = await ensureInit()
      amp.reset?.()
    },

    async track(
      eventName: string,
      properties: Record<string, unknown>
    ): Promise<void> {
      const amp = await ensureInit()
      amp.track(eventName, properties)
    },

    async identify(userId: string, properties?: UserProperties): Promise<void> {
      const amp = await ensureInit()
      amp.setUserId(userId)

      try {
        const identifyPayload = new AmplitudeIdentify()
        if (properties?.name) {
          identifyPayload.set('user_name', properties.name)
          identifyPayload.set('name', properties.name)
        }
        if (properties?.email) {
          identifyPayload.set('user_email', properties.email)
          identifyPayload.set('email', properties.email)
        }
        await amp.identify(identifyPayload).promise
      } catch (error) {
        console.error('Failed to identify user in Amplitude', error)
      }
    },

    async setGroup(
      groupKey: string,
      groupId: string | string[]
    ): Promise<void> {
      const amp = await ensureInit()
      amp.setGroup(groupKey, groupId)
    },

    async identifyGroup(
      groupKey: string,
      groupId: string,
      properties?: GroupProperties
    ): Promise<void> {
      const amp = await ensureInit()

      try {
        // Set group properties (identity is already established by groupKey/groupId params)
        const groupIdentify = new AmplitudeIdentify()
        if (properties?.name) {
          groupIdentify.set('name', properties.name)
          groupIdentify.set(`${groupKey}_name`, properties.name)
        }
        await amp.groupIdentify(groupKey, groupId, groupIdentify).promise

        // Also set user properties for segmentation by group
        const userIdentify = new AmplitudeIdentify()
        userIdentify.set(`${groupKey}_id`, groupId)
        if (properties?.name) {
          userIdentify.set(`${groupKey}_name`, properties.name)
        }
        await amp.identify(userIdentify).promise
      } catch (error) {
        console.error('Failed to identify group in Amplitude', error)
      }
    },

    async clearGroup(groupKey: string): Promise<void> {
      const amp = await ensureInit()
      amp.setGroup(groupKey, [])
    },
  }
}
